import { Fragment } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useFrozenValue } from "../hooks/useFrozenValue";

export default function ImportErrorsDialog({ open, errors, onClose }) {
  const displayedErrors = useFrozenValue(errors, errors.length > 0);
  const rowCount = new Set(displayedErrors.map((err) => err.row)).size;

  const styles = {
    title: {
      color: "background.text",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
    titleIcon: { color: "error.main" },
    background: { bgcolor: "background.dialog", color: "background.text" },
    summary: { mb: 2 },
    list: { maxHeight: 320, overflowY: "auto" },
    listItem: { alignItems: "center", gap: 1.5, py: 1 },
    rowChip: { flexShrink: 0, mt: "2px" },
    fieldLabel: { color: "background.textMuted", lineHeight: "inherit" },
    message: { color: "background.text" },
    closeBtn: { m: 1, textTransform: "none" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.background } }}
    >
      <DialogTitle sx={styles.title}>Import failed</DialogTitle>
      <DialogContent>
        <Alert severity="error" variant="outlined" sx={styles.summary}>
          Nothing was imported. {displayedErrors.length} issue
          {displayedErrors.length !== 1 ? "s" : ""} found across {rowCount} row
          {rowCount !== 1 ? "s" : ""} — fix these and try again.
        </Alert>
        <List dense sx={styles.list}>
          {displayedErrors.map((err, index) => (
            <Fragment key={index}>
              {index > 0 && <Divider component="li" />}
              <ListItem disableGutters sx={styles.listItem}>
                <Chip
                  label={`Row ${err.row}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={styles.rowChip}
                />
                <ListItemText
                  slotProps={{
                    primary: { variant: "body2", sx: styles.message },
                  }}
                  primary={
                    <>
                      {err.field && (
                        <Typography
                          component="span"
                          variant="overline"
                          sx={styles.fieldLabel}
                        >
                          {err.field}:{" "}
                        </Typography>
                      )}
                      {err.message}
                    </>
                  }
                />
              </ListItem>
            </Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={styles.closeBtn}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
